import React from "react";
import { Box, Typography } from "@mui/material";
import Icon from "@mui/material/Icon";

import { RISK_FROM_ACCENT, RiskFromFeatureKind } from "./riskFromStyles";

export interface RiskFromHoverCardProps {
  /** Pointer position in the map container (layout px). */
  x: number;
  y: number;
  /** Container size, to keep the card on screen. */
  containerWidth: number;
  containerHeight: number;
  kind: RiskFromFeatureKind;
  swatchColor: string;
  primaryLabel: string;
  primaryValue: string;
  secondaryLabel: string;
  secondaryValue: string;
  /** "Mean of 12 scenarios" for ensembles. */
  note?: string | null;
  loaded: boolean;
}

const WIDTH = 196;
const HEIGHT_ESTIMATE = 128;
const OFFSET = 14;

/**
 * Floating card beside the pointer while it rests on an ignition: the active
 * metric first, the other metric under it, and what a click will do.
 */
const RiskFromHoverCard: React.FC<RiskFromHoverCardProps> = (p) => {
  // Right of and below the pointer by default; flip when that would leave
  // the map.
  const left = p.x + OFFSET + WIDTH > p.containerWidth ? p.x - OFFSET - WIDTH : p.x + OFFSET;
  const top =
    p.y + OFFSET + HEIGHT_ESTIMATE > p.containerHeight ? p.y - OFFSET - HEIGHT_ESTIMATE : p.y + OFFSET;

  return (
    <Box
      sx={{
        position: "absolute",
        left,
        top,
        width: WIDTH,
        boxSizing: "border-box",
        padding: "10px 12px",
        borderRadius: "10px",
        backgroundColor: "rgba(17, 22, 33, 0.96)",
        boxShadow: "0 0 0 1px rgba(255,255,255,0.08), 0 10px 24px rgba(0,0,0,0.45)",
        pointerEvents: "none",
        zIndex: 1002,
        display: "flex",
        flexDirection: "column",
        gap: 0.5,
        color: "#f8fafc",
        fontVariantNumeric: "tabular-nums",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Box
          sx={{
            width: 10,
            height: 10,
            flexShrink: 0,
            borderRadius: p.kind === "point" ? "50%" : "3px",
            backgroundColor: p.swatchColor,
            boxShadow: "0 0 0 1px rgba(255,255,255,0.25)",
          }}
        />
        <Typography sx={{ fontSize: "0.75rem", color: "#94a3b8", lineHeight: 1.2 }}>
          {p.primaryLabel}
        </Typography>
      </Box>
      <Typography sx={{ fontSize: "1.25rem", fontWeight: 600, letterSpacing: "-0.01em", lineHeight: 1.2 }}>
        {p.primaryValue}
      </Typography>
      <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1, fontSize: "0.75rem" }}>
        <Typography sx={{ fontSize: "inherit", color: "#94a3b8" }}>{p.secondaryLabel}</Typography>
        <Typography sx={{ fontSize: "inherit", color: "#e2e8f0", fontWeight: 500 }}>
          {p.secondaryValue}
        </Typography>
      </Box>
      {p.note && (
        <Typography sx={{ fontSize: "0.68rem", color: "#64748b", lineHeight: 1.2 }}>{p.note}</Typography>
      )}
      <Box sx={{ height: "1px", backgroundColor: "rgba(255,255,255,0.08)", my: 0.5 }} />
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, color: RISK_FROM_ACCENT, fontSize: "0.75rem" }}>
        <Icon sx={{ fontSize: 14 }}>ads_click</Icon>
        <span>{p.loaded ? "Loaded in the player" : "Click to load this fire"}</span>
      </Box>
    </Box>
  );
};

export default RiskFromHoverCard;
